import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Card, CardMedia, CircularProgress, Box, Avatar, Stack, Button, Tooltip, Table, TableRow, TableBody, TableCell, FormControlLabel, Checkbox } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faVideo, faVideoSlash, faMicrophoneSlash } from "@fortawesome/free-solid-svg-icons";
import './WaitingRoom.css';
import posthog from 'posthog-js';
import { Title, getText, getPracticeText, languageMapping, WaitingContent, RoundInfo } from "./WaitingRoomConst"

/*
/interview/?round=prod-sense
/interview/?round=hr-round
/interview/?round=customized/?job=${jobId}
*/

const pricingUrl = process.env.REACT_APP_FRONTEND_URL + "/pricing";


const WaitingRoom = ({
    isVideoOnGlobal,
    setVideoGlobal,
    isMicOnGlobal,
    setMicGlobal,
    setHasUserJoined,
    round,
    candidate,
    candidateImg,
    language,
    quickMockJobId,
    companyName,
    jobTitle,
    isSignedIn,
    hasTwoInterviewsThisMonth,
    setJoinTime,
    isB2BInterview,
    jobId,
    isPracticeMode,
    practiceQuestion,
    practiceQuestionType,
    practiceInterviewRound,
    practiceDifficulty,
    isPremium
}) => {

    const [isMobile, setIsMobile] = useState(false);
    const [introductionTitle, setIntroductionTitle] = useState(null);
    const [introductionContent, setIntroductionContent] = useState(null);
    useEffect(() => {
        const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        setIsMobile(isMobileDevice);
        if (isMobileDevice) {
            alert("For the best experience, please use a laptop or desktop to join the interview.");
        }
    }, []);

    useEffect(() => {
        if (!jobId) return;
        console.log(process.env.REACT_APP_BACKEND_URL_MIRWORK2B);
        axios.get(`${process.env.REACT_APP_BACKEND_URL_MIRWORK2B}/api/bData/job?jobId=${jobId}`)
            .then(res => {
                const job = res.data.data;
                if (job.introductionTitle) setIntroductionTitle(job.introductionTitle);
                if (job.introductionContent) setIntroductionContent(job.introductionContent);
            })
            .catch(err => {
                console.warn('[WaitingRoom] Could not fetch job intro content:', err.message);
            });
    }, [jobId]);

    const videoRef = useRef(null);
    const [isVideoOn, setVideo] = useState(isVideoOnGlobal);
    const [isMicOn, setMic] = useState(isMicOnGlobal);
    const [languageDisplay, setLanguageDisplay] = useState("English");
    const [isJoinEnabled, setJoinEnabled] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(true);

    useEffect(() => {
        if (isVideoOn) {
            turnVideoOn();
        } else {
            turnVideoOff();
        }
    }, [isVideoOn]);

    useEffect(() => {
        setLanguageDisplay(languageMapping[language] || "English");
    }, [language]);

    useEffect(() => {
        // For Practice mode, enable join if we have practice question data
        if (isPracticeMode) {
            setJoinEnabled(!!practiceQuestion);
            return;
        }
        // For B2B interviews, always enable join as long as we have a company name and job title
        if (isB2BInterview && companyName && jobTitle) {
            setJoinEnabled(true);
            return;
        }

        // Original B2C logic
        if (round === 'Quick Mock') {
            if (quickMockJobId) {
                setJoinEnabled(true);
            } else {
                setJoinEnabled(false);
            }
        } else {
            setJoinEnabled(true);
        }
    }, [quickMockJobId, round, isB2BInterview, companyName, jobTitle, isPracticeMode, practiceQuestion]);


    const turnVideoOn = () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices.getUserMedia({ video: { aspectRatio: 16 / 9 } })
                .then(stream => {
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                    }
                })
                .catch(error => {
                    console.error('Error accessing the camera', error);
                });
        }
    };

    const turnVideoOff = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const videoTracks = videoRef.current.srcObject.getVideoTracks();
            if (videoTracks.length > 0) {
                videoTracks[0].stop();
            }
        }
    };

    const micClick = () => {
        setMic(!isMicOn);
        setMicGlobal(!isMicOn);
    }

    const videoClick = () => {
        setVideo(!isVideoOn);
        setVideoGlobal(!isVideoOn);
    }

    const onJoin = () => {
        posthog.capture('interview_click_join_button', {});
        setHasUserJoined(true);
        setJoinTime(Date.now());
    }

    const handlePrivacyChange = (event) => {
        setPrivacyAccepted(event.target.checked);
    };

    const Icon = (
        <Stack className="waitingRoom-icon-box" direction="horizontal">
            <Button
                variant="contained"
                size="large"
                className={!isMicOn ? "waitingRoom-icons-active" : "waitingRoom-icons"}
                onClick={micClick}
            >
                <Tooltip title={isMicOn ? "Mute" : "Unmute"}>
                    <FontAwesomeIcon icon={!isMicOn ? faMicrophoneSlash : faMicrophone} />
                </Tooltip>
            </Button>

            <Button
                variant="contained"
                size="large"
                className={"waitingRoom-icons" + (!isVideoOn ? "-active" : "")}
                onClick={videoClick}
            >
                <Tooltip title={isVideoOn ? "Stop Video" : "Start Video"}>
                    <FontAwesomeIcon icon={!isVideoOn ? faVideoSlash : faVideo} />
                </Tooltip>
            </Button>
        </Stack>
    );

    return (
        <Box className="waitingRoom">
            <Box className="waitingRoom-video-box">
                {
                    isVideoOn &&
                    <Card elevation={0} className={`waitingRoom-cardMedia`}>
                        <CardMedia component="video" ref={videoRef} autoPlay playsInline />
                        <Box className="mainScreen-nameTagBox">
                            <Typography className="mainScreen-nameTagText">{candidate}</Typography>
                        </Box>
                        {Icon}
                    </Card>
                }
                {
                    !isVideoOn &&
                    <Box className={`waitingRoom-video-off-background`}>
                        {
                            candidateImg ?
                                <Avatar src={candidateImg} className="mainScreen-cand-avatar" /> :
                                <Avatar className="mainScreen-cand-avatar"> CAND </Avatar>
                        }
                        <Box className="mainScreen-nameTagBox">
                            <Typography className="mainScreen-nameTagText">{candidate}</Typography>
                        </Box>
                        {Icon}
                    </Box>
                }


            </Box>

            <Stack direction="column" spacing={3} className="waitingRoom-infoCard-box">
                <Box sx={{ mb: 3 }}>
                    <Typography className="title-text-thin">
                        {isPracticeMode
                            ? "🎯 Practice Interview Mode"
                            : isB2BInterview
                                ? `${companyName} Interview`
                                : introductionTitle || Title
                        }
                    </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                    {isPracticeMode ? (
                        getPracticeText(companyName, jobTitle, practiceQuestion, isPremium)
                    ) : (
                        <Typography className="normal-text">
                            {introductionContent || getText(jobTitle, companyName)}
                        </Typography>
                    )}
                </Box>
                <Table className="waitingRoom-table">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ borderBottom: "none" }} className="normal-text waitingRoom-tableCellOne">
                                Interview Type
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }} className="normal-text waitingRoom-tableCellTwo">
                                {isPracticeMode
                                    ? `${practiceQuestionType}${practiceInterviewRound ? ` • ${practiceInterviewRound}` : ''}`
                                    : isB2BInterview
                                        ? 'Job Interview'
                                        : RoundInfo(round)[0]
                                }
                            </TableCell>
                        </TableRow >
                        <TableRow>
                            <TableCell className="normal-text waitingRoom-tableCellOne">Duration</TableCell>
                            <TableCell className="normal-text waitingRoom-tableCellTwo">
                                {isPracticeMode
                                    ? '15-20 minutes (Practice)'
                                    : isB2BInterview
                                        ? '20-30 minutes'
                                        : RoundInfo(round)[1]
                                }
                            </TableCell>
                        </TableRow>
                        <TableCell className="normal-text waitingRoom-tableCellOne">Language</TableCell>
                        <TableCell className="normal-text waitingRoom-tableCellTwo">
                            {languageDisplay}
                        </TableCell>
                    </TableBody>
                </Table>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={privacyAccepted}
                            onChange={handlePrivacyChange}
                            color="primary"
                        />
                    }
                    label={
                        <Typography className="normal-text">
                            I agree to the{' '}
                            <a
                                href="https://www.mirwork.ai/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline' }}
                            >
                                Privacy Policy
                            </a>
                        </Typography>
                    }
                />
                {!isJoinEnabled && !isB2BInterview && (
                    <Box display="flex" alignItems="center" mb={0}>
                        <CircularProgress size={20} style={{ marginRight: 8 }} />
                        <Typography variant="body2">
                            {WaitingContent}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {hasTwoInterviewsThisMonth && !isB2BInterview && (
                        <Typography className="normal-secondary-alert">
                            You've reached the interview limit for this month.&nbsp;
                            <a href={pricingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                                Upgrade your plan
                            </a>
                        </Typography>
                    )}
                    <Button variant="contained" onClick={onJoin} className="main-button-middle"
                        disabled={!isJoinEnabled || !privacyAccepted || (!isSignedIn && !isB2BInterview) || (hasTwoInterviewsThisMonth && !isB2BInterview) || isMobile}>
                        {isPracticeMode ? "Start Practice" : "Join Call"}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default WaitingRoom;